/** @jsxImportSource @emotion/react */
import * as React from 'react';
import * as R from 'ramda';
import * as Buttons from '../../../../forms/elements/buttons';
import { css } from '@emotion/react';
import { Loader } from '../../../../forms/elements/loader/Loader';
import { GuestDetails } from '../../../guestDetails';
import { fullName } from '../../../module';
import ModalView, { ModalContent } from '../../../../layout/modal/ModalView';
import printer from '../../../../assets/icons/printer.svg';
import { BoxedErrorInverse } from '../../../../forms/elements/boxedMessage/boxedMessageStyles';
import { StyledBoxedMessage } from '../../../../forms/elements/boxedMessage/StyledBoxedMessage';
import { MODAL_SIZE } from '../../../../layout/modal/modalSize';
import { connect } from 'react-redux';
import { IAppState } from '../../../../core/redux/IAppState';
import { ACTION_TYPE } from '../../../../core/redux/actionType';
import { clearApiErrorAction } from '../../../../core/redux/api/clearApiErrorAction';
import { getError, getLoading } from '../../../../core/redux/api/module';
import { calculatePassReprintFee, DEFAULT_REPRINT_FEE, getPrintCountStartDate, NO_REPRINT_FEE, pdfForGuest, printPdf } from './module';
import { IKON_MODAL } from '../../../../core/modal/ikonModal';
import { showModalAction } from '../../../../core/modal/showModalAction';
import { isNilOrEmpty, assoc } from '../../../../language/module';
import { PassPdf } from './passPdf';
import { reissueMediaAction } from './reissueMediaAction';
import { markGuestPdfPrintedAction } from './markGuestPdfPrintedAction';
import { Column, Row } from '../../../../layout/flexbox';
import { COLUMN_SPACING, ROW_SPACING } from '../../../../forms/styles';
import ErrorView from '../../../../forms/ErrorView';
import { LargeText, MediumText } from '../../../../styles/styles';
import { passPrintDisabledMessage } from '../../../featureDisabling';
import ModalTitleHeader from '../../../../layout/modal/ModalTitleHeader';
import { EntitlementType } from '../../../../entitlementTypes/entitlementType';
import ImmutableMap from '../../../../language/ImmutableMap';
import { getResource } from '../../../../language/resources/getResource';
import { Dropdown } from '../../../../forms/elements/select/Dropdown';
import { QuickEntitlement } from '../../models';
import { BLUE_CREEK, RED_CREEK } from '../../../../styles/colors';
import { getSeasonReprintCountAction } from './getSeasonReprintCountAction';
import { ApiError } from '../../../../core/redux/api/apiError';
import { PassPrintConfig } from '../../../../auth/config/passPrintConfig';
import { formatDate } from '../../../../language/time';


const MainContent = css`
  ${MediumText};
  ${COLUMN_SPACING};
  
  * {
    ${MediumText};
  }
`;

const reprintFeeBox = (fee: number | undefined) => css`
  margin-left: 10px;
  width: 45px;
  min-height: 20px;
  background-color: ${((fee !== undefined && fee > NO_REPRINT_FEE) || fee === undefined) ? RED_CREEK : BLUE_CREEK};
  border-radius: 5px;
  text-align: center;
  padding: 7px 0px !important;
  color: WHITE;
`;

const passSelectorWrapper = css`
  margin-left: 125px;
  margin-bottom: 65px;
`;

const reprintRow = css`
  margin-top: 10px;
  display: flex;
  align-items: center;
`;

const buttonsRow = css`
  max-height: 50px;
  margin-top: 25px;
`;


/**
 * Function accepting the current modal state, and returning the appropriate message to give to the Okay button.
 */

type Props = {
  guestId: string;
  entitlementId?: number;
  guest?: GuestDetails;
  isLoadingReprintCount?: boolean;
  reprintCountError?: ApiError;

  guestPrintPassPDF?: PassPdf;
  isLoadingPdf?: boolean;
  printError?: string | undefined;

  allEntitlementTypes?: ImmutableMap<EntitlementType>;
  resources: Record<string, any>;
  enablePassReprintNotifications: boolean;
  passPrintConfig: PassPrintConfig;
  dateFormat: string;

  // Dispatch
  onDismiss: () => void;
  showModal?: (modalType: IKON_MODAL, extras?: any) => void;
  clearPassError?: (guestId: string) => void;
  printPass?: (mediaId: string) => void;
  markPrinted?: (pdf: PassPdf) => void;
  getSeasonReprintCount?: (printCountStartDate : Date, guestId : string) => void;
  clearError?: (requestId: string, actionType: string) => void;
};

type State = {
  error: string | undefined;
  selectedEntitlement: QuickEntitlement | undefined;
  confirmedToPrint: boolean;
  passReprintFee: number | undefined;
  passReprintCountError: string | undefined;
};

/**
 * PassPrintModal: a component that manages printing state transitions in the form of a finite state machine. Uses its
 * props to decide when, how and if the user has printed to update its messages and available CTAs.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect(
  (state: IAppState, props: Props) => ({
    guest: state.guests.get(props.guestId),
    isLoadingReprintCount: !!getLoading(
      state,
      props.guestId,
      ACTION_TYPE.GET_SEASON_REPRINT_COUNT_REQUEST,
    ),
    reprintCountError: getError(
      state,
      props.guestId,
      ACTION_TYPE.GET_SEASON_REPRINT_COUNT_REQUEST,
    ),
    entitlementId: state.resortToolsConfig.dtwCategoryId,
    isLoadingPdf: !!getLoading(
      state,
      props.guestId,
      ACTION_TYPE.REISSUE_MEDIA_REQUEST
    ),
    printError: R.path(
      ["errorMsg"],
      getError(state, props.guestId, ACTION_TYPE.REISSUE_MEDIA_REQUEST)
    ),
    guestPrintPassPDF: pdfForGuest(state, props.guestId),
    allEntitlementTypes:
      state.entitlementTypesDetails &&
      state.entitlementTypesDetails.allEntitlementTypes,
    resources: state.locale,
    enablePassReprintNotifications:
      state.resortToolsConfig.enablePassReprintNotifications,
    passPrintConfig: state.resortToolsConfig.passPrintConfig,
    dateFormat: state.defaultDateFormat,
  }),
  (dispatch, props) => ({
    showModal: (modalType: IKON_MODAL, extras?: any) =>
      dispatch(showModalAction(modalType, extras)),
    printPass: (entitlementTypeId: string) =>
      dispatch(
        reissueMediaAction([{ guestId: props.guestId, entitlementTypeId }])
      ),
    markPrinted: (pdf: PassPdf) => dispatch(markGuestPdfPrintedAction(pdf)),
    clearPassError: () =>
      dispatch(
        clearApiErrorAction(props.guestId, ACTION_TYPE.REISSUE_MEDIA_REQUEST)
      ),
    getSeasonReprintCount: (printCountStartDate: Date, guestId: string) =>
      dispatch(
        getSeasonReprintCountAction(printCountStartDate, guestId)
      ),
    clearError: (requestId: string, actionType: string) => dispatch(clearApiErrorAction(requestId, actionType)),
  })
)(
  class PassPrintModal extends React.PureComponent<Props, State> {
    public state = {
      error: passPrintDisabledMessage(
        this.props.resources,
        this.props.entitlementId!,
        this.props.guest,
        this.props.allEntitlementTypes
      ),
      selectedEntitlement: this.props.guest?.entitlements?.filter(
        (e) =>
          e.entitlementCategoryId === 1 &&
          e.entitlementStatusId === 1 &&
          Date.parse(e.expirationDate) > Date.now()
      )[0],
      confirmedToPrint: false,
      passReprintFee: undefined,
      passReprintCountError: undefined,
    };

    public guestEntitlements = Array.from(
      new Map<number, QuickEntitlement>(
        this.props.guest?.entitlements
          ?.filter(
            (e) =>
              e.entitlementCategoryId === 1 &&
              e.entitlementStatusId === 1 &&
              Date.parse(e.expirationDate) > Date.now()
          )
          .map<[number, QuickEntitlement]>((e) => [e.entitlementTypeId, e])
      ).values()
    );

    public getEntitlementTypeId = (item: { entitlementTypeId?: string }): string =>
      item.entitlementTypeId ?? "";


    public getEntitlementDescription = (item): string => {
      const entitlement = this.props.allEntitlementTypes?.get(
        this.getEntitlementTypeId(item)
      );
      return (
        (entitlement?.seasonDescription || "") +
        " " +
        entitlement?.publicDescription
      );
    };

    public onEntitlementChange = (entitlementTypeId) =>
      this.setState((prev) =>
        assoc("selectedEntitlement", entitlementTypeId, prev)
      );

    public okayButtonTextFor = (hasConfirmed: boolean): string =>
      hasConfirmed
        ? getResource(this.props.resources, "PASS_PRINT_MODAL_OK_BTN_CLOSE")
        : getResource(this.props.resources, "PASS_PRINT_MODAL_OK_BTN_PRINT");

    public printPass = () =>
      this.props.printPass!(
        R.path<string>(["entitlementTypeId"], this.state.selectedEntitlement) ||
          ""
      );

    public getSeasonReprintCount = () => {
      const printCountStartDate = getPrintCountStartDate(this.props.passPrintConfig);
      this.props.getSeasonReprintCount!(printCountStartDate, this.props.guestId);
    };

    public onFinishPdfPrint = (success: boolean) => {
      if (!success) {
        this.setState((prev) =>
          assoc(
            "error",
            getResource(this.props.resources, "PRINT_FAILURE"),
            prev
          )
        );
        return;
      }

      this.props.showModal!(IKON_MODAL.GUEST_RFID, {
        guestId: this.props.guestId,
      });
    };

    public startPDFPrintInNewTab = (passPdf: string): void =>
      printPdf(window, passPdf, this.onFinishPdfPrint);

    public cancelAndClose = () => {
      this.props.clearPassError!(this.props.guest!.masterGuestId);
      this.props.onDismiss();
    };

    private onOkClick = () => {
      // If error, ok => close
      if (this.state.error || this.props.printError) {
        this.cancelAndClose();
        return;
      }

      // Otherwise, ok => confirm to print
      this.setState((prev) =>
        R.mergeDeepRight(prev, {
          error: undefined,
          confirmedToPrint: true,
        })
      );

      this.printPass();
    };

    /**
     * Pure function accepting the component's state, and returning the appropriate print modal content to render in
     * render(). CTAs are handled separately inside render().
     */
    public mainContentFor = (props: Props, state: State): React.ReactNode => {
      // Validation Error
      if (!isNilOrEmpty(this.state.error)) {
        return <ErrorView error={state.error} boxStyle={BoxedErrorInverse} />;
      }

      // API Error
      if (!isNilOrEmpty(props.printError)) {
        return (
          <ErrorView
            error={`${getResource(this.props.resources, "API_ERROR")}\n(${
              props.printError
            })`}
          />
        );
      }

      // Confirmation
      if (!state.confirmedToPrint) {
        const name = fullName(props.guest);

        return (
          <React.Fragment>
            <StyledBoxedMessage
              message={getResource(
                this.props.resources,
                "PRINT_CONFIRMATION_2"
              )}
              boxStyle={BoxedErrorInverse}
            />
            <span
              css={css`
                display: block;
              `}
            >
              {getResource(this.props.resources, "PRINT_CONFIRMATION_1", name)}
            </span>
          </React.Fragment>
        );
      }

      return (
        <Column
          alignItems="center"
          justifyContent="center"
          rootCss={COLUMN_SPACING}
        >
          <Loader />
          <span css={LargeText}>
            {getResource(this.props.resources, "PASS_PRINT_MODAL_GENERATE_PDF")}
          </span>
        </Column>
      );
    };

    private renderPassSelector = () => (
      <Dropdown
        resources={this.props.resources}
        items={this.guestEntitlements ? this.guestEntitlements : []}
        getKey={this.getEntitlementTypeId}
        getValue={this.getEntitlementTypeId}
        getLabel={this.getEntitlementDescription}
        noItemsMessage={getResource(
          this.props.resources,
          "PRINT_NO_ENTITLEMENT"
        )}
        onChange={this.onEntitlementChange}
        selectedItem={this.state.selectedEntitlement}
        rootCss={css`
          width: 350px;
          max-width: 100%;
        `}
      />
    );

    private renderButtons = () => (
      <Row justifyContent="flex-end" rootCss={ROW_SPACING}>
        {
          // Cancel Print Button
          !this.state.confirmedToPrint && (
            <Buttons.BigSecondary
              type="button"
              onClick={this.cancelAndClose}
              title={getResource(
                this.props.resources,
                "PASS_PRINT_MODAL_CANCEL_TITLE"
              )}
            >
              {getResource(this.props.resources, "PASS_PRINT_MODAL_CANCEL")}
            </Buttons.BigSecondary>
          )
        }
        {/* Okay button */}
        <Buttons.BigNeutral
          type="button"
          onClick={this.onOkClick}
        >
          {this.okayButtonTextFor(this.state.confirmedToPrint)}
        </Buttons.BigNeutral>
      </Row>
    );

    public componentDidMount () {
      if (this.props.enablePassReprintNotifications) {
        this.getSeasonReprintCount();
      }
    };

    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
       if (!prevProps.reprintCountError && this.props.reprintCountError) {
        this.setState(prev => assoc('passReprintCountError', getResource(this.props.resources, 'REPRINT_COUNT_LOAD_ERROR'), prev))
        this.setState(prev => assoc('passReprintFee', undefined, prev))
        this.props.clearError!(this.props.reprintCountError.requestId, this.props.reprintCountError.actionType);  
      }

      /* Calculate reprint fee - if unable to load pass reprtings - default to 0 */
      if (!this.props.isLoadingReprintCount) {
        if (this.props.guest?.passReprintCount !== undefined) {
          this.setState((prev) => assoc("passReprintFee", calculatePassReprintFee(this.props.guest?.passReprintCount!, this.props.passPrintConfig?.defaultPassPrintFee), prev));
        } else {
          this.setState((prev) => assoc("passReprintFee", undefined, prev));
        }
      }

      // Guest changed, we have to re-validate
      if (prevProps.guest !== this.props.guest) {
        passPrintDisabledMessage(
          this.props.resources,
          this.props.entitlementId!,
          this.props.guest,
          this.props.allEntitlementTypes
        );
      }

      // A usable Print Pass PDF came through
      if (
        !R.equals(prevProps.guestPrintPassPDF, this.props.guestPrintPassPDF) && // PDF changed
        !isNilOrEmpty(R.path<string>(['pdf'], this.props.guestPrintPassPDF)) // PDF data is legit
      ) {
        // Open PDF in new tab for printing
        this.startPDFPrintInNewTab(this.props.guestPrintPassPDF!.pdf);
        this.props.markPrinted!(this.props.guestPrintPassPDF!);
      }
    }

    public render = () => (
      <ModalView
        onDismiss={this.props.onDismiss}
        dismissable={!this.props.isLoadingPdf}
        contentCss={MainContent}
        size={this.props.isLoadingPdf ? MODAL_SIZE.X_SMALL : MODAL_SIZE.MEDIUM}
      >
        <ModalTitleHeader
          resources={this.props.resources}
          heading={getResource(
            this.props.resources,
            "PASS_PRINT_MODAL_HEADING"
          )}
          icon={printer}
          onDismiss={this.props.onDismiss}
        />
        <ModalContent>
          {/* Print Modal Content */}
          {this.mainContentFor(this.props, this.state)}

          {(this.props.enablePassReprintNotifications) ?
           
            // Print Modal CTAs
            !this.props.isLoadingPdf && (
            <Column>
              <div css={passSelectorWrapper}>{this.renderPassSelector()}</div>
              <ErrorView error={this.state.passReprintCountError}/>
              <Column rootCss={css`margin-bottom: 50px;`}>
                <Row justifyContent="space-between" rootCss={ROW_SPACING}>
                  <Column rootCss={css`margin-top: 20px;`}>
                    <span>{getResource(this.props.resources, 'REPRINT_POLICY_FLAT_RATE', this.props.passPrintConfig?.defaultPassPrintFee ?? DEFAULT_REPRINT_FEE)}</span>
                    <div css={reprintRow}>
                      <span css={css`margin-top: 5px;`}>{getResource(this.props.resources, 'REPRINT_APPLICABLE_FEES')}</span>
                      <div 
                        css={reprintFeeBox(this.state.passReprintFee)}
                        title={this.state.passReprintFee === undefined ? getResource(this.props.resources, 'REPRINT_COUNT_LOAD_ERROR') : undefined} >
                          {this.state.passReprintFee !== undefined ? `$${this.state.passReprintFee}` : undefined}
                      </div>
                    </div>
                    <span css={css`margin-top: 15px;`}>{getResource(this.props.resources, 'REPRINT_PASSES_PRINTED_SINCE', formatDate(getPrintCountStartDate(this.props.passPrintConfig), this.props.dateFormat), this.props.guest?.passReprintCount ?? 0)}</span>
                  </Column>
                  <div css={buttonsRow}>
                    {this.renderButtons()}
                  </div>
                </Row>
              </Column>
            </Column>
            )
           :
          
            // Print Modal CTAs
            !this.props.isLoadingPdf && (
              <Row justifyContent="flex-end" rootCss={ROW_SPACING}>
                {this.renderPassSelector()}
                {this.renderButtons()}
              </Row>
            )
          
        }
        </ModalContent>
      </ModalView>
    );
  }
);
